import { getClient, AuthError } from '../utils/client';
import { success } from '../utils/logger';
import Table from 'cli-table3';
import chalk from 'chalk';
import { formatJSON, formatNushell } from '../utils/formatter';

export interface ListRowsOptions {
  limit?: number;
  offset?: number;
  search?: string;
  filters?: string;
  fieldNames?: boolean;
  format?: 'table' | 'json' | 'nushell';
}

export interface CreateRowOptions {
  data: string;
  fieldNames?: boolean;
}

export interface UpdateRowOptions {
  data: string;
  fieldNames?: boolean;
}

/**
 * List rows from a Baserow table.
 * @param tableId The ID of the table.
 * @param options List options.
 * @returns The rows result object.
 */
export async function listRows(tableId: number, options: ListRowsOptions): Promise<any> {
  const client = await getClient();
  const params: Record<string, any> = {
    user_field_names: options.fieldNames ?? false
  };
  if (options.limit) {
    params.size = options.limit;
  }
  if (options.offset) {
    params.offset = options.offset;
  }
  if (options.search) {
    params.search = options.search;
  }
  if (options.filters) {
    try {
      const filtersObj = JSON.parse(options.filters);
      if (Array.isArray(filtersObj)) {
        params.filter_object = filtersObj.map(filter => ({
          field: filter.field,
          type: filter.type,
          value: filter.value
        }));
      } else {
        params.filter_object = [{
          field: filtersObj.field,
          type: filtersObj.type,
          value: filtersObj.value
        }];
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        throw new Error(`Invalid filter format: ${err.message}`);
      } else {
        throw new Error('Invalid filter format: An unknown error occurred');
      }
    }
  }
  const rows = await client.databaseRows.list(tableId, params);
  return rows;
}

/**
 * Create a new row in a Baserow table.
 * @param tableId The ID of the table.
 * @param options Create options.
 * @returns The created row object.
 */
export async function createRow(tableId: number, options: CreateRowOptions): Promise<any> {
  const client = await getClient();
  let rowData: Record<string, any>;
  try {
    rowData = JSON.parse(options.data);
  } catch (err: unknown) {
    throw new Error(`Invalid JSON data: ${err instanceof Error ? err.message : 'An unknown error occurred'}`);
  }
  const params = {
    userFieldNames: options.fieldNames ?? false
  };
  const newRow = await client.databaseRows.create(tableId, rowData, params);
  return newRow;
}

/**
 * Updates an existing row in a Baserow table.
 * @param tableId The ID of the table.
 * @param rowId The ID of the row to update.
 * @param options Update options.
 * @returns The updated row object.
 */
export async function updateRow(tableId: number, rowId: number, options: UpdateRowOptions): Promise<any> {
  const client = await getClient();
  let rowData: Record<string, any>;
  try {
    rowData = JSON.parse(options.data);
  } catch (err: unknown) {
    throw new Error(`Invalid JSON data: ${err instanceof Error ? err.message : 'An unknown error occurred'}`);
  }
  const updatedRow = await client.databaseRows.update(
    tableId,
    rowId,
    rowData,
    {
      userFieldNames: options.fieldNames ?? false
    }
  );
  return updatedRow;
} 